import {QuoteDiscountEditor} from "./QuoteDiscounts";
import { useState, useRef, useEffect } from "react";
import { useRealtimeQuotes } from "@/lib/useRealtimeCases";
import { supabase } from "@/lib/supabaseClient";
import { PdfPreviewModal } from "./PdfPreviewModal";
import { EditableFileName } from "./EditableFileName";
import { useToast } from "@/lib/useToast";
import { Toast } from "@/components/ui";

import { ArchiveDocumentButton, ArchivedDocuments } from "./ArchivedDocuments";
import { setDocumentsArchived } from "@/lib/documentArchive";

interface QuoteUploadProps {
  caseId: string;
}

function CopySignLinkButton({ signatureToken }: { signatureToken?: string }) {
  const [copied, setCopied] = useState(false);

  if (!signatureToken) return null;

  const publicUrl = `${window.location.origin}/sign/${signatureToken}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      // On utilise un toast depuis le parent QuoteUpload qui est importé
      alert("Erreur lors de la copie du lien"); // Garde alert car composant séparé
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
      title="Copier le lien de signature publique"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
        />
      </svg>
      {copied ? "✓ Lien copié !" : "Copier le lien de signature"}
    </button>
  );
}

export function QuoteUpload({ caseId }: QuoteUploadProps) {
  const { quotes, archivedQuotes, loading, refresh } = useRealtimeQuotes(caseId);
  const [discountQueue,setDiscountQueue]=useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [hasLegacyQuotes, setHasLegacyQuotes] = useState(false);
  const [showLegacyQuotes, setShowLegacyQuotes] = useState(false);
  const legacyUrls = quotes.filter((quote) => quote.version === 1).map((quote) => quote.file_url);
  useEffect(() => {
    let active = true;
    setHasLegacyQuotes(false);
    supabase.from("cases").select("old_airtable_quotes").eq("id", caseId).maybeSingle().then(({ data }) => {
      if (active) setHasLegacyQuotes(Boolean(data?.old_airtable_quotes));
    });
    return () => { active = false; };
  }, [caseId]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast, showToast, hideToast } = useToast();

  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          reject(new Error("Lecture du fichier impossible"));
          return;
        }
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = () => {
        reject(reader.error ?? new Error("Lecture du fichier impossible"));
      };
      reader.readAsDataURL(file);
    });

  const handleUpload = async (file: File, uploadGroupId?: string, groupPosition?: number) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      showToast({ message: "Seuls les fichiers PDF sont acceptés", type: "warning" });
      return;
    }

    // Vérifier la taille (3 Mo max)
    const MAX_SIZE = 3 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      showToast({ message: "Fichier trop volumineux. Taille maximale : 3 Mo", type: "warning" });
      return;
    }

    try {
      const fileBase64 = await toBase64(file);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      const response = await fetch("/api/quotes/upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          caseId,
          fileName: file.name,
          fileType: file.type || "application/pdf",
          fileBase64,
          uploadGroupId,
          groupPosition,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.message ?? "Erreur lors de l'upload du devis");
      }

      if(payload.quoteId)setDiscountQueue(q=>[...q,payload.quoteId]);
      return payload; // Retourner le payload pour récupérer uploadGroupId
    } catch (err) {
      console.error("Erreur lors de l'upload:", err);
      const message = err instanceof Error ? err.message : "Erreur lors de l'upload du devis";
      throw new Error(message);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter(f => f.type === "application/pdf");
    if (files.length > 0) {
      setUploading(true);
      try {
        let sharedGroupId: string | undefined;
        
        // Upload tous les fichiers avec le même upload_group_id
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const result = await handleUpload(file, sharedGroupId, i + 1);
          
          // Récupérer le uploadGroupId du premier fichier pour les suivants
          if (i === 0 && result?.uploadGroupId) {
            sharedGroupId = result.uploadGroupId;
          }
        }
        
        const versionLabel = files.length > 1 
          ? `${files.length} devis uploadés avec succès !`
          : `Devis uploadé avec succès !`;
        showToast({ message: versionLabel, type: "success" });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Erreur lors de l'upload";
        showToast({ message, type: "error" });
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => f.type === "application/pdf");
    if (files.length > 0) {
      setUploading(true);
      try {
        let sharedGroupId: string | undefined;
        
        // Upload tous les fichiers avec le même upload_group_id
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const result = await handleUpload(file, sharedGroupId, i + 1);
          
          // Récupérer le uploadGroupId du premier fichier pour les suivants
          if (i === 0 && result?.uploadGroupId) {
            sharedGroupId = result.uploadGroupId;
          }
        }
        
        const versionLabel = files.length > 1 
          ? `${files.length} devis uploadés avec succès !`
          : `Devis uploadé avec succès !`;
        showToast({ message: versionLabel, type: "success" });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Erreur lors de l'upload";
        showToast({ message, type: "error" });
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const changeArchive = async (ids: string[], archived: boolean) => {
    setArchiving(true);
    try {
      await setDocumentsArchived("quote", ids, archived);
      await refresh();
      showToast({ message: archived ? "Devis archivé." : "Devis restauré.", type: "success" });
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Modification impossible.", type: "error" });
    } finally { setArchiving(false); }
  };

  const handleUpdateDisplayName = async (quoteId: string, newName: string) => {
    const { error } = await supabase
      .from("quotes")
      .update({ display_name: newName })
      .eq("id", quoteId);

    if (error) throw error;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        Chargement des devis...
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {hasLegacyQuotes && legacyUrls.length > 0 && <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <span>{legacyUrls.length} devis importé{legacyUrls.length > 1 ? "s" : ""} depuis Airtable</span>
        <button type="button" onClick={() => setShowLegacyQuotes(true)} className="rounded-md px-3 py-2 font-medium hover:bg-amber-100">Voir les devis</button>
      </div>}
      <PdfPreviewModal isOpen={showLegacyQuotes && legacyUrls.length > 0} onClose={() => setShowLegacyQuotes(false)} pdfUrls={legacyUrls} title="Devis Airtable" />
      {/* Zone de drop */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-colors
          ${
            dragActive
              ? "border-indigo-500 bg-indigo-50"
              : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
          }
          ${uploading ? "opacity-50 cursor-not-allowed" : ""}
        `}
      >
        <div className="text-4xl mb-2">📄</div>
        <div className="text-sm font-medium text-gray-700 mb-1">
          {uploading
            ? "Upload en cours..."
            : "Déposez vos devis PDF ou cliquez pour importer"}
        </div>
        <div className="text-xs text-gray-500">
          Sélection multiple possible • Format PDF uniquement • 3 Mo max par fichier
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
        />
      </div>

      {/* Liste des versions */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Historique des versions ({quotes.length})
        </h4>
        {quotes.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">
            Aucun devis uploadé
          </div>
        ) : (
          <div className="space-y-2">
            {quotes.map((quote) => (
              <div
                key={quote.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="text-2xl flex-shrink-0">📄</div>
                    <div className="flex-1 min-w-0">
                      {/* Nom éditable */}
                      <div className="flex items-center gap-2 mb-1">
                        <EditableFileName
                          initialName={quote.display_name || `Version ${quote.version}`}
                          onSave={(newName) => handleUpdateDisplayName(quote.id, newName)}
                        />
                        {quote.signed && (
                          <span className="flex-shrink-0 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            ✓ Signé
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {quote.sent_at
                          ? `Envoyé le ${formatDateTime(quote.sent_at)}`
                          : `Créé le ${formatDateTime(quote.created_at)}`}
                      </div>
                    </div>
                  </div>
                  <a
                    href={quote.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    Télécharger
                  </a>
                </div>

                {/* Bouton copier le lien de signature */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button type="button" onClick={()=>setDiscountQueue([quote.id])} className="rounded-md border border-indigo-200 px-3 py-2 text-sm font-medium text-indigo-700">Remises</button>
                  <CopySignLinkButton signatureToken={quote.signature_token} />
                  <ArchiveDocumentButton busy={archiving} onArchive={() => changeArchive([quote.id], true)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!uploading && discountQueue[0] && <QuoteDiscountEditor key={discountQueue[0]} caseId={caseId} quoteId={discountQueue[0]} onClose={()=>setDiscountQueue(q=>q.slice(1))}/> }
      <ArchivedDocuments title="Devis archivés" busy={archiving}
        documents={archivedQuotes.map((quote) => ({ id: quote.id, ids: [quote.id], name: quote.display_name || `Devis v${quote.version}`, version: quote.version, archivedAt: quote.archived_at, urls: [quote.file_url], status: quote.signed ? "Signé" : undefined }))}
        onRestore={(ids) => changeArchive(ids, false)} />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
      />
    </div>
  );
}

